import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { attachmentMime, countImageAttachments, fileHeadline, formatFileDate, isImage, isPdf } from './healthUtils';
import { attachmentsForDownload } from './healthDownloadUtils';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import HealthFileEditDialog from './HealthFileEditDialog';

function buildAttachmentPreviewItems(attachments) {
  const items = [];
  for (let i = 0; i < attachments.length; i += 1) {
    const a = attachments[i];
    const mime = attachmentMime(a);
    const url = a.url;
    if (!url) continue;
    items.push({ url, key: a.key, isImage: isImage(mime), mimeType: mime });
  }
  return items;
}

function openPreview(attachment, attachments, onPreview) {
  const items = buildAttachmentPreviewItems(attachments);
  if (items.length === 0) return;
  const mime = attachmentMime(attachment);
  const url = attachment.url;
  if (!url) return;
  let index = 0;
  for (let i = 0; i < items.length; i += 1) {
    if (attachment.key && items[i].key === attachment.key) {
      index = i;
      break;
    }
    if (!attachment.key && items[i].url === url) {
      index = i;
      break;
    }
  }
  onPreview({
    url,
    key: attachment.key,
    isImage: isImage(mime),
    mimeType: mime,
    items,
    index,
  });
}

export default function HealthFileCard({
  file,
  fieldOptions,
  request,
  onUpdate,
  onDelete,
  onPreview,
  downloadMode,
  downloadSelected,
  onToggleDownloadSelect,
}) {
  const attachments = Array.isArray(file.attachments) ? file.attachments : [];
  const [editOpen, setEditOpen] = useState(false);
  const [pdfCreating, setPdfCreating] = useState(false);
  const imageAttachmentCount = countImageAttachments(attachments);
  const canDownload = attachmentsForDownload(file).length > 0;
  const isDownloadSelectable = downloadMode && canDownload;

  async function createPdfFromImages() {
    if (pdfCreating || imageAttachmentCount === 0) return;
    setPdfCreating(true);
    try {
      const res = await request(`/api/health/files/${file.id}/pdf-from-images`, { method: 'POST' });
      if (!res.ok) return;
      onUpdate(await res.json());
    } catch (e) {
      // ignore
    } finally {
      setPdfCreating(false);
    }
  }

  const tagChips = [];
  if (Array.isArray(file.tags)) {
    for (let i = 0; i < file.tags.length; i += 1) {
      const t = file.tags[i];
      const label = typeof t === 'string' ? t : t.name;
      if (label) tagChips.push(label);
    }
  }

  const headline = fileHeadline(file);
  const showTypeChip = Boolean(file.report_type && file.report_type.trim() && file.type);

  function handleCardClick() {
    if (isDownloadSelectable && onToggleDownloadSelect) {
      onToggleDownloadSelect(file.id);
    }
  }

  return (
    <>
      <Card
        variant="outlined"
        onClick={isDownloadSelectable ? handleCardClick : undefined}
        sx={{
          height: '100%',
          minWidth: 0,
          overflow: 'visible',
          width: '100%',
          cursor: isDownloadSelectable ? 'pointer' : 'default',
          borderColor: downloadSelected ? 'primary.main' : undefined,
          borderWidth: downloadSelected ? 2 : 1,
          opacity: downloadMode && !canDownload ? 0.45 : 1,
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, position: 'relative' }}>
          {downloadMode && canDownload && (
            <Checkbox
              checked={!!downloadSelected}
              onChange={() => onToggleDownloadSelect && onToggleDownloadSelect(file.id)}
              onClick={(e) => e.stopPropagation()}
              sx={{ position: 'absolute', top: 4, right: 4, p: 0.5 }}
            />
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            {attachments.length === 0 && (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ImageIcon sx={{ color: 'text.disabled' }} />
              </Box>
            )}
            {attachments.map((a, i) => {
              const mime = attachmentMime(a);
              return (
                <Box
                  key={a.key || i}
                  onClick={(e) => {
                    if (downloadMode) {
                      e.stopPropagation();
                      return;
                    }
                    openPreview(a, attachments, onPreview);
                  }}
                  role="button"
                  sx={{
                    position: 'relative',
                    width: 56,
                    height: 56,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: downloadMode ? 'default' : 'pointer',
                  }}
                >
                  {isImage(mime) && a.url ? (
                    <img src={a.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : isPdf(mime) ? (
                    <PictureAsPdfIcon sx={{ fontSize: 28 }} />
                  ) : (
                    <PictureAsPdfIcon sx={{ fontSize: 28 }} />
                  )}
                </Box>
              );
            })}
          </Stack>

          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.5} sx={{ minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {headline && (
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {headline}
                </Typography>
              )}
              {file.file_name && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: headline ? 0.25 : 0 }}>
                  {file.file_name}
                </Typography>
              )}
            </Box>
            {!downloadMode && (
              <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
                <IconButton size="small" onClick={() => setEditOpen(true)} title="Edit" aria-label="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
                {imageAttachmentCount > 0 && (
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={createPdfFromImages}
                    disabled={pdfCreating}
                    title="Create PDF from images"
                    aria-label="Create PDF from images"
                  >
                    {pdfCreating ? <CircularProgress size={18} /> : <PictureAsPdfIcon fontSize="small" />}
                  </IconButton>
                )}
                <IconButton size="small" color="error" onClick={() => onDelete(file.id)} title="Delete" aria-label="Delete">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
            {file.date && <Chip label={formatFileDate(file.date)} size="small" variant="outlined" title="Document date" />}
            {showTypeChip && <Chip label={file.type} size="small" variant="outlined" />}
          </Stack>

          {file.hospital_name && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Hospital – {file.hospital_name}
            </Typography>
          )}

          {tagChips.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
              {tagChips.map((label) => (
                <Chip key={label} label={label} size="small" sx={{ height: 20 }} />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <HealthFileEditDialog
        open={editOpen}
        file={file}
        onClose={() => setEditOpen(false)}
        onSaved={onUpdate}
        request={request}
        fieldOptions={fieldOptions}
      />
    </>
  );
}
